import { memo, useCallback, useEffect, useRef, useState, type FC } from "react";
import Box from "@mui/material/Box";
import { FilterMethod } from "@/generated/company-search";
import { useFeatureFlag } from "@/hooks/useLaunchDarkly";
import {
  useCompanyFilterActions,
  useCompanyFilterOptions,
  useCompanyFilterSlice,
} from "../../hooks";
import {
  defaultUploadedAccountsSlice,
  type UploadedAccountsSlice,
} from "./slice";
import type { UploadedAccountsOptions } from "./options";
import { useUploadPolling } from "./hooks";
import type { CompanyConfigSlice } from "../companyConfig";
import {
  UploadDropzone,
  UploadingState,
  SuccessState,
  ErrorState,
  MatchingOptions,
} from "./components";

export const UploadedAccountsFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice(
    "uploadedAccounts"
  ) as UploadedAccountsSlice;
  const { setSlice } = useCompanyFilterActions();
  const options = useCompanyFilterOptions("uploadedAccounts") as
    | UploadedAccountsOptions
    | undefined;
  const companyConfig = useCompanyFilterSlice(
    "companyConfig"
  ) as CompanyConfigSlice;

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const previousUploadState = useRef(slice.uploadState);

  // Show success banner when transitioning to SUCCESS, auto-dismiss after 3s
  useEffect(() => {
    if (
      slice.uploadState === "SUCCESS" &&
      previousUploadState.current === "PROCESSING"
    ) {
      setShowSuccessBanner(true);
      const timer = setTimeout(() => {
        setShowSuccessBanner(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
    previousUploadState.current = slice.uploadState;
  }, [slice.uploadState]);

  // Polling hook
  useUploadPolling({
    fileId: slice.fileId,
    isProcessing: slice.uploadState === "PROCESSING",
    onPoll: options?.onPoll,
    onSuccess: useCallback(
      (stats) => {
        setSlice("uploadedAccounts", (prev) => ({
          ...prev,
          uploadState: "SUCCESS",
          matchedRows: stats.matchedRows,
          invalidRows: stats.invalidRows,
          totalRows: stats.totalRows,
          uploadedAt: stats.uploaded,
          matchedAt: stats.lastMatched,
          isPlatformPermissible: stats.isPlatformPermissible ?? null,
          isV1Dataset: stats.isV1Dataset ?? null,
          rgifExclusiveCompany: stats.rgifExclusiveCompany ?? null,
        }));
      },
      [setSlice]
    ),
    onError: useCallback(
      (error) => {
        setSlice("uploadedAccounts", (prev) => ({
          ...prev,
          uploadState: "ERROR",
          error: error.message,
        }));
      },
      [setSlice]
    ),
  });

  const handleFileAccepted = useCallback(
    async (files: File[]) => {
      if (files.length === 0 || !options?.onUpload) return;

      const [file] = files;
      setSelectedFile(file);

      // Auto-start upload immediately - preserve options from current state
      setSlice("uploadedAccounts", (prev) => ({
        ...defaultUploadedAccountsSlice,
        uploadState: "UPLOADING",
        includeHierarchy: prev.includeHierarchy,
        enforceCountryMatch: prev.enforceCountryMatch,
        filterMethod: prev.filterMethod,
      }));

      try {
        // Get fresh values at upload time. Like OppGen, no country
        // enforcement (company-match-api's rematch drops it).
        const result = await options.onUpload(
          file,
          slice.includeHierarchy,
          false,
          companyConfig
        );
        setSlice("uploadedAccounts", (prev) => ({
          ...prev,
          uploadState: "PROCESSING",
          fileId: result.fileId,
          fileName: result.fileName,
          totalRows: result.totalRows,
        }));
        setSelectedFile(null);
      } catch (error) {
        setSlice("uploadedAccounts", (prev) => ({
          ...prev,
          uploadState: "ERROR",
          error: error instanceof Error ? error.message : "Upload failed",
        }));
      }
    },
    [options, slice.includeHierarchy, setSlice, companyConfig]
  );

  const handleHierarchyChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      setSlice("uploadedAccounts", (prev) => ({
        ...prev,
        includeHierarchy: checked,
      }));
    },
    [setSlice]
  );

  const handleFilterMethodChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSlice("uploadedAccounts", (prev) => ({
        ...prev,
        filterMethod: event.target.checked
          ? FilterMethod.EXCLUDE_MATCHED
          : FilterMethod.FILTER,
      }));
    },
    [setSlice]
  );

  // OppGen offers ADD_FLAG only with the Universe export, the only consumer
  // that marks matched companies (covered-wagon).
  const universeEnabled = useFeatureFlag("universeJsonExport", false);

  const handleAddFlagChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSlice("uploadedAccounts", (prev) => ({
        ...prev,
        filterMethod: event.target.checked
          ? FilterMethod.ADD_FLAG
          : FilterMethod.FILTER,
      }));
    },
    [setSlice]
  );

  const handleRetry = useCallback(() => {
    setSlice("uploadedAccounts", defaultUploadedAccountsSlice);
  }, [setSlice]);

  const handleClear = useCallback(() => {
    setSlice("uploadedAccounts", defaultUploadedAccountsSlice);
    setSelectedFile(null);
  }, [setSlice]);

  const handleDownloadDetails = useCallback(
    async (fileId: string) => {
      if (!options?.onDownloadDetails) return;
      await options.onDownloadDetails(fileId);
    },
    [options]
  );

  const { uploadState } = slice;
  const isLoading = uploadState === "UPLOADING" || uploadState === "PROCESSING";
  const showMatchingOptions = !isLoading;

  return (
    <Box sx={{ mx: -2, mt: -2 }}>
      {uploadState === "IDLE" && (
        <UploadDropzone onFilesAccepted={handleFileAccepted} />
      )}

      {isLoading && (
        <UploadingState
          uploadState={uploadState as "UPLOADING" | "PROCESSING"}
          fileName={slice.fileName || selectedFile?.name || null}
          totalRows={slice.totalRows}
        />
      )}

      {uploadState === "SUCCESS" && (
        <SuccessState
          fileName={slice.fileName}
          totalRows={slice.totalRows}
          matchedRows={slice.matchedRows}
          invalidRows={slice.invalidRows}
          uploadedAt={slice.uploadedAt}
          matchedAt={slice.matchedAt}
          showSuccessBanner={showSuccessBanner}
          fileId={slice.fileId}
          onClear={handleClear}
          onDownloadDetails={handleDownloadDetails}
        />
      )}

      {uploadState === "ERROR" && (
        <ErrorState error={slice.error} onRetry={handleRetry} />
      )}

      {showMatchingOptions && (
        <MatchingOptions
          includeHierarchy={slice.includeHierarchy}
          onHierarchyChange={handleHierarchyChange}
          filterMethod={slice.filterMethod}
          onFilterMethodChange={handleFilterMethodChange}
          showAddFlag={
            universeEnabled || slice.filterMethod === FilterMethod.ADD_FLAG
          }
          onAddFlagChange={handleAddFlagChange}
        />
      )}
    </Box>
  );
});

UploadedAccountsFilter.displayName = "UploadedAccountsFilter";
